import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShiftsService } from '../services/supabase/shiftsService';
import { CareReportSubmissionService } from '../services/supabase/careReportSubmissionService';
import { mapShiftRow } from '../utils/shiftUtils';
import { determinePriceByDate, minsInSpanEdited, hoursInSpan } from '../utils/hoursUtils';
import './payrollPage.css';
import './payrollAllPage.css';

function formatDatetime(datetimeStr) {
  if (!datetimeStr) return '—';
  return new Date(datetimeStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function PayrollAllPage({ clientList, caretakerList, draft, onDraftChange }) {
  const { selectedCaretakers, startDate, endDate, results } = draft;
  const setSelectedCaretakers = (v) => onDraftChange(d => ({ ...d, selectedCaretakers: v }));
  const setStartDate           = (v) => onDraftChange(d => ({ ...d, startDate: v }));
  const setEndDate             = (v) => onDraftChange(d => ({ ...d, endDate: v }));
  const setResults             = (v) => onDraftChange(d => ({ ...d, results: v }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editRow, setEditRow] = useState(null); // { shiftId, caretakerName }
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const activeCaretakers = (caretakerList ?? [])
    .filter(c => !c.isDeactivated)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleGenerate = async () => {
    if (!selectedCaretakers || selectedCaretakers.length === 0) {
      setError('Please select at least one caretaker.');
      return;
    }
    if (!startDate) { setError('Please select a start date.'); return; }
    if (!endDate)   { setError('Please select an end date.'); return; }
    if (endDate < startDate) { setError('End date must be after start date.'); return; }

    setError('');
    setLoading(true);
    setResults(null);

    try {
      const startMs = new Date(startDate).getTime();
      const endMs   = new Date(endDate).getTime() + 24 * 60 * 60 * 1000;

      const groups = await Promise.all(
        selectedCaretakers.map(async (caretakerName) => {
          const caretaker = activeCaretakers.find(c => c.name === caretakerName);
          if (!caretaker) return null;

          const rawShifts = await ShiftsService.getShiftsForCaretakerInRange(caretaker.email, startMs, endMs);
          const shifts = rawShifts.map(mapShiftRow);

          const uniqueClients = [...new Set(shifts.map(s => s.client))];
          const allReports = (await Promise.all(
            uniqueClients.map(c => CareReportSubmissionService.getReportsForClient(c))
          )).flat();

          const shiftRows = shifts
            .sort((a, b) => new Date(a.start) - new Date(b.start))
            .map(s => {
              const shiftMs  = new Date(s.start).getTime();
              const rate     = s.isFlatFee ? null : determinePriceByDate(caretaker.pay, shiftMs);
              const mins     = s.isFlatFee ? 0 : minsInSpanEdited([s], { excludeCleaning: false });
              const hours    = s.isFlatFee ? null : parseFloat(hoursInSpan(mins));
              const lineTotal = s.isFlatFee ? null : parseFloat((hours * rate).toFixed(2));
              const flatFee  = s.isFlatFee
                ? (Number(s.flatFeeAmount) || Number(s.flatFeeAmountClient) || 0)
                : null;
              const mileage  = Number(s.shiftMileage) || 0;
              const report   = allReports.find(r => r.shift_id === s.docID);
              const reimbursement = report ? (Number(report.supplies_amount) || 0) : 0;

              return {
                id: s.docID,
                caretakerName,
                start: s.start,
                end: s.editedEndTime || s.end,
                client: s.client,
                isFlatFee: s.isFlatFee,
                hours,
                rate,
                lineTotal,
                flatFee,
                mileage,
                reimbursement,
                _raw: s,
                _report: report ?? null,
              };
            });

          const totalHoursAmt      = shiftRows.filter(r => !r.isFlatFee).reduce((sum, r) => sum + (r.lineTotal || 0), 0);
          const totalFlatFees      = shiftRows.reduce((sum, r) => sum + (r.flatFee || 0), 0);
          const totalMileage       = shiftRows.reduce((sum, r) => sum + r.mileage, 0) * 0.45;
          const totalReimbursements = shiftRows.reduce((sum, r) => sum + r.reimbursement, 0);
          const grandTotal         = totalHoursAmt + totalFlatFees + totalMileage + totalReimbursements;

          return { caretakerName, shiftRows, totalHoursAmt, totalFlatFees, totalMileage, totalReimbursements, grandTotal };
        })
      );

      setResults({ groups: groups.filter(Boolean) });
    } catch (err) {
      console.error(err);
      setError('Failed to generate payroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row) => {
    setEditRow({ shiftId: row.id, caretakerName: row.caretakerName });
    setSaveError('');
    setEditValues({
      start:         row._raw.editedStartTime || row._raw.start,
      end:           row._raw.editedEndTime   || row._raw.end,
      client:        row._raw.client,
      mileage:       row.mileage,
      flatFee:       row.flatFee ?? '',
      reimbursement: row.reimbursement,
    });
  };

  const handleSave = async () => {
    if (!editRow) return;
    const group    = results?.groups.find(g => g.caretakerName === editRow.caretakerName);
    const row      = group?.shiftRows.find(r => r.id === editRow.shiftId);
    if (!row) return;
    setSaving(true);
    setSaveError('');
    try {
      const updates = {
        edited_start_time: editValues.start,
        edited_end_time:   editValues.end,
        client:            editValues.client,
        shift_mileage:     Number(editValues.mileage) || 0,
      };
      if (row.isFlatFee) updates.flat_fee_amount = Number(editValues.flatFee) || 0;
      await ShiftsService.editShiftInfo(editRow.shiftId, updates);

      const newReimbursement = Number(editValues.reimbursement) || 0;
      if (row._report) {
        await CareReportSubmissionService.submitReport({ ...row._report, supplies_amount: newReimbursement });
      } else if (newReimbursement > 0) {
        await CareReportSubmissionService.submitReport({
          shift_id:             editRow.shiftId,
          client_name:          editValues.client,
          caretaker_email:      row._raw.caretakerEmail,
          shift_start:          editValues.start,
          shift_end:            editValues.end,
          mileage:              0,
          supplies_description: '',
          supplies_amount:      newReimbursement,
          important_information: '',
          custom_answers:       {},
        });
      }
      setEditRow(null);
      await handleGenerate();
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = n => `$${Number(n).toFixed(2)}`;

  const sortedClients = [...(clientList ?? [])]
    .filter(c => !c.is_deactivated)
    .sort((a, b) => a.name.localeCompare(b.name));

  const editRowData = editRow
    ? results?.groups.find(g => g.caretakerName === editRow.caretakerName)?.shiftRows.find(r => r.id === editRow.shiftId)
    : null;

  const periodLabel = startDate && endDate
    ? `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  return (
    <div className="payrollPageDiv">
      <div className="payrollPageHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="payrollPageTitle">Payroll — All Caretakers</h2>
      </div>

      <div className="formSep"></div>

      <div className="payrollFormSection">
        <h3 className="payrollFormTitle">Generate Payroll</h3>

        <div className="payrollFormGrid paFormGrid">
          <label className="payrollLabel">Caretakers</label>
          <div className="paCheckboxList">
            {activeCaretakers.map(c => (
              <label key={c.docID} className="paCheckboxItem">
                <input
                  type="checkbox"
                  className="paCheckbox"
                  checked={selectedCaretakers.includes(c.name)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedCaretakers([...selectedCaretakers, c.name]);
                    } else {
                      setSelectedCaretakers(selectedCaretakers.filter(n => n !== c.name));
                    }
                  }}
                />
                {c.name}
              </label>
            ))}
          </div>

          <label className="payrollLabel">Start Date</label>
          <input
            type="date" className="payrollInput"
            value={startDate} onChange={e => setStartDate(e.target.value)}
          />

          <label className="payrollLabel">End Date</label>
          <input
            type="date" className="payrollInput"
            value={endDate} onChange={e => setEndDate(e.target.value)}
          />
        </div>

        {error && <p className="payrollError">{error}</p>}

        <button className="payrollGenerateBtn" onClick={handleGenerate} disabled={loading}>
          {loading ? 'Generating…' : 'Generate'}
        </button>
      </div>

      {results && (
        <>
          <div className="formSep"></div>
          <div className="payrollResultsSection">
            <h3 className="payrollResultsTitle">{periodLabel}</h3>

            {results.groups.map(group => (
              <div key={group.caretakerName} className="paCaretakerSection">
                <h4 className="paCaretakerHeader">{group.caretakerName}</h4>

                {group.shiftRows.length === 0 ? (
                  <p className="payrollEmpty">No shifts found in the selected date range.</p>
                ) : (
                  <div className="payrollTableWrapper">
                    <table className="payrollTable">
                      <thead>
                        <tr>
                          <th className="prTh">Start</th>
                          <th className="prTh">End</th>
                          <th className="prTh">Client</th>
                          <th className="prTh prRight">Hours</th>
                          <th className="prTh prRight">Rate</th>
                          <th className="prTh prRight">Hours Total</th>
                          <th className="prTh prRight">Flat Fee</th>
                          <th className="prTh prRight">Mileage</th>
                          <th className="prTh prRight">Reimbursements</th>
                          <th className="prTh"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.shiftRows.map(row => (
                          <tr key={row.id} className="prRow">
                            <td className="prTd">{formatDatetime(row.start)}</td>
                            <td className="prTd">{formatDatetime(row.end)}</td>
                            <td className="prTd">{row.client}</td>
                            <td className="prTd prRight">{row.hours != null ? row.hours.toFixed(2) : '—'}</td>
                            <td className="prTd prRight">{row.rate != null ? `$${Number(row.rate).toFixed(2)}` : '—'}</td>
                            <td className="prTd prRight">{row.lineTotal != null ? fmt(row.lineTotal) : '—'}</td>
                            <td className="prTd prRight">{row.flatFee != null ? fmt(row.flatFee) : '—'}</td>
                            <td className="prTd prRight">{row.mileage > 0 ? `${row.mileage} mi` : '—'}</td>
                            <td className="prTd prRight">{row.reimbursement > 0 ? fmt(row.reimbursement) : '—'}</td>
                            <td className="prTd">
                              <button className="prEditBtn" onClick={() => openEdit(row)}>Edit</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="payrollSummary">
                  <div className="prSummaryRow">
                    <span className="prSummaryLabel">Hours Subtotal</span>
                    <span className="prSummaryValue">{fmt(group.totalHoursAmt)}</span>
                  </div>
                  <div className="prSummaryRow">
                    <span className="prSummaryLabel">Flat Fee Shifts</span>
                    <span className="prSummaryValue">{fmt(group.totalFlatFees)}</span>
                  </div>
                  <div className="prSummaryRow">
                    <span className="prSummaryLabel">Mileage (@ $0.45/mi)</span>
                    <span className="prSummaryValue">{fmt(group.totalMileage)}</span>
                  </div>
                  <div className="prSummaryRow">
                    <span className="prSummaryLabel">Reimbursements</span>
                    <span className="prSummaryValue">{fmt(group.totalReimbursements)}</span>
                  </div>
                  <div className="prSummaryRow prTotalRow">
                    <span className="prSummaryLabel">Total</span>
                    <span className="prSummaryValue">{fmt(group.grandTotal)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editRow && (
        <div className="prModalOverlay" onClick={() => setEditRow(null)}>
          <div className="prModal" onClick={e => e.stopPropagation()}>
            <h3 className="prModalTitle">Edit Shift</h3>

            <div className="prModalGrid">
              <label className="prModalLabel">Start Time</label>
              <input
                type="datetime-local" className="prModalInput"
                value={editValues.start ? editValues.start.slice(0, 16) : ''}
                onChange={e => setEditValues(v => ({ ...v, start: e.target.value }))}
              />

              <label className="prModalLabel">End Time</label>
              <input
                type="datetime-local" className="prModalInput"
                value={editValues.end ? editValues.end.slice(0, 16) : ''}
                onChange={e => setEditValues(v => ({ ...v, end: e.target.value }))}
              />

              <label className="prModalLabel">Client</label>
              <select
                className="prModalInput"
                value={editValues.client}
                onChange={e => setEditValues(v => ({ ...v, client: e.target.value }))}
              >
                {sortedClients.map(c => (
                  <option key={c.docID} value={c.name}>{c.name}</option>
                ))}
              </select>

              <label className="prModalLabel">Mileage (mi)</label>
              <input
                type="number" className="prModalInput"
                value={editValues.mileage}
                onChange={e => setEditValues(v => ({ ...v, mileage: e.target.value }))}
              />

              {editRowData?.isFlatFee && (
                <>
                  <label className="prModalLabel">Flat Fee ($)</label>
                  <input
                    type="number" className="prModalInput"
                    value={editValues.flatFee}
                    onChange={e => setEditValues(v => ({ ...v, flatFee: e.target.value }))}
                  />
                </>
              )}

              <label className="prModalLabel">Reimbursements ($)</label>
              <input
                type="number" className="prModalInput"
                value={editValues.reimbursement}
                onChange={e => setEditValues(v => ({ ...v, reimbursement: e.target.value }))}
              />
            </div>

            {saveError && <p className="prModalError">{saveError}</p>}

            <div className="prModalActions">
              <button className="prModalCancel" onClick={() => setEditRow(null)}>Cancel</button>
              <button className="prModalSave" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
